import { useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { SpinnerGap, CheckCircle, XCircle, X, WarningCircle } from "phosphor-react"
import adAccountsService from "../../services/adAccountsService"

const kindMeta = (kind) => {
  switch (kind) {
    case "meta_bm":
      return { title: "Meta BM", valueLabel: "BM ID" }
    case "tiktok_bc":
      return { title: "TikTok BC", valueLabel: "BC ID" }
    case "email":
      return { title: "Email", valueLabel: "Email" }
    default:
      return { title: kind || "Identifier", valueLabel: "Value" }
  }
}

export default function ApproveRejectModal({
  isOpen,
  onClose,
  identifier, // { kind, identifierId, value, accountId, index }
  onSuccess,
}) {
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState("")
  const [rejectionReason, setRejectionReason] = useState("")
  const meta = useMemo(() => kindMeta(identifier?.kind), [identifier?.kind])

  const payload = useMemo(() => {
    return {
      kind: identifier?.kind,
      accountId: identifier?.accountId,
      AdaccountId: identifier?.AdaccountId,

      identifierId: identifier?.identifierId,
      value: identifier?.value,
      index: Number.isInteger(identifier?.index) ? identifier.index : undefined,
    }
  }, [identifier])

  const submitDecision = async (decision) => {
    if (!payload?.kind || !payload?.accountId) return
    setErrorMsg("")

    try {
      setLoading(true)

      await adAccountsService.updateIdentifierDecision({
        decision,
        rejectionReason: decision === "rejected" ? rejectionReason : undefined,
        ...payload,
      })

      setLoading(false)
      onClose?.()
      onSuccess?.()
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to update decision."
      console.error("Identifier decision update failed:", e)
      setErrorMsg(msg)
      setLoading(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <motion.div
            className="w-full max-w-md rounded-2xl border border-neutral-200/60 dark:border-neutral-900 bg-white dark:bg-neutral-950 p-5 shadow-xl"
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.98 }}
            transition={{ duration: 0.18 }}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-sm font-medium text-neutral-900 dark:text-white">
                  Review {meta.title}
                </h3>
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  Choose <span className="font-medium">Approve</span> or{" "}
                  <span className="font-medium">Reject</span>. This will lock the decision.
                </p>
              </div>

              <button
                onClick={onClose}
                disabled={loading}
                className="p-2 rounded-xl hover:bg-neutral-100 dark:hover:bg-neutral-900 disabled:opacity-60"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            {errorMsg && (
              <div className="mt-4 rounded-xl border border-red-200/60 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300 flex gap-2">
                <WarningCircle size={18} className="shrink-0 mt-0.5" />
                <div className="leading-relaxed">{errorMsg}</div>
              </div>
            )}

            <div className="mt-4 rounded-xl bg-neutral-50 dark:bg-neutral-900/40 p-3 space-y-3">
              <div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400">Type</div>
                <div className="text-sm font-medium text-neutral-900 dark:text-white">
                  {meta.title}
                </div>
              </div>

              <div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400">
                  {meta.valueLabel}
                </div>
                <div className="text-sm font-medium text-neutral-900 dark:text-white break-all">
                  {payload?.value || "N/A"}
                </div>
              </div>

              <div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400">
                  Ad Account ID
                </div>
                <div className="text-sm font-medium text-neutral-900 dark:text-white break-all">
                  {payload?.AdaccountId || "N/A"}
                </div>
              </div>
            </div>
            {identifier && (
              <div className="mt-4">
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter rejection reason..."
                  className="w-full p-3 border rounded-xl text-sm"
                />
              </div>
            )}

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={onClose}
                disabled={loading}
                className="h-10 px-4 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 text-sm text-neutral-800 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-900 disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                onClick={() => submitDecision("rejected")}
                disabled={loading || !rejectionReason.trim()}
                className="h-10 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm inline-flex items-center gap-2 disabled:opacity-60"
              >
                {loading ? <SpinnerGap size={16} className="animate-spin" /> : <XCircle size={16} />}
                Reject
              </button>

              <button
                onClick={() => submitDecision("approved")}
                disabled={loading}
                className="h-10 px-4 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm inline-flex items-center gap-2 disabled:opacity-60"
              >
                {loading ? <SpinnerGap size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                Approve
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
